<template>
  <div class="ppv" :class="`ppv--${mode}`" :data-test="mode === 'page' ? 'patient-profile-view' : null">
    <PatientPanelHeader
      :patient="patient"
      :appointment="appointment"
      :appointment-meta="appointmentMeta"
      :insurance="insurance"
      :tiles="tiles"
      :attention-note="attentionNote"
      :today="today || ''"
      :date-format="dateFormat"
      :profile-link="profileLink"
      :closable="mode === 'panel'"
      @close="$emit('close')"
    >
      <template v-if="$scopedSlots.avatar" #avatar><slot name="avatar"></slot></template>
      <template v-if="$scopedSlots['header-extra']" #extra><slot name="header-extra"></slot></template>
      <template v-if="$scopedSlots['verified-action']" #verified-action><slot name="verified-action"></slot></template>
      <template #actions><slot name="actions"></slot></template>
    </PatientPanelHeader>

    <nav class="pp-tabs" role="tablist" aria-label="Patient sections" data-test="pp-tabs">
      <button
        v-for="t in tabList"
        :key="t.id"
        type="button"
        role="tab"
        class="pp-tab"
        :class="{ 'pp-tab--on': tab === t.id }"
        :aria-selected="tab === t.id ? 'true' : 'false'"
        :disabled="t.disabled"
        :title="t.disabled ? 'Coming soon' : null"
        :data-test="`pp-tab-btn-${t.id}`"
        @click="$emit('tab', t.id)"
      >
        {{ t.label }}
        <span v-if="counts[t.id]" class="pp-tab__n">{{ counts[t.id] }}</span>
      </button>
    </nav>

    <div class="pp-pad">
      <div v-if="dataLoading && !payload" class="pp-state" data-test="pp-loading">Loading this patient…</div>

      <div v-else-if="dataError" class="pp-state pp-state--error" data-test="pp-error">
        <p>{{ dataError }}</p>
        <button type="button" class="pp-retry" @click="$emit('retry')">Try again</button>
      </div>

      <div v-else-if="unavailable" class="pp-state" data-test="pp-unavailable">
        No Open Dental data has been loaded for this practice yet.
      </div>

      <div v-else-if="payload && !patient" class="pp-state" data-test="pp-not-found">
        This patient could not be found in Open Dental.
      </div>

      <template v-else-if="payload">
        <!-- A persistent tab's body is drawn below (kept alive); nothing to switch to here. -->
        <span v-if="persistentTabs.indexOf(tab) !== -1" class="ppv__persist-mark"></span>
        <!-- A host can replace any tab's body (the page's editable Follow-ups, Family). -->
        <slot v-else-if="$scopedSlots[`tab-${tab}`]" :name="`tab-${tab}`"></slot>
        <OverviewTab
          v-else-if="tab === 'overview'"
          :payload="payload"
          :appointment="appointment"
          :appointment-meta="appointmentMeta"
          :insurance="insurance"
          :insurance-loading="insuranceLoading"
          :followups="followups"
          :date-format="dateFormat"
          :timezone="timezone"
          :today="today || ''"
          @tab="$emit('tab', $event)"
        >
          <template v-if="$scopedSlots['overview-followups']" #followups="scope">
            <slot name="overview-followups" v-bind="scope"></slot>
          </template>
        </OverviewTab>
        <FollowupsTab
          v-else-if="tab === 'followups'"
          :followups="followups || []"
          :today="today || ''"
          :date-format="dateFormat"
        />
        <TreatmentTab
          v-else-if="tab === 'treatment'"
          :payload="payload"
          :alert-details="alertDetails"
          :signed-plans="signedPlans"
          :signed-plans-loading="signedPlansLoading"
          :tx-presenter-on="!!(capabilities && capabilities.txsign)"
          :date-format="dateFormat"
          :timezone="timezone"
        >
          <template v-if="$scopedSlots['treatment-side']" #side><slot name="treatment-side"></slot></template>
        </TreatmentTab>
        <AppointmentsTab v-else-if="tab === 'appointments'" :payload="payload" :date-format="dateFormat" />
        <CommunicationTab v-else-if="tab === 'communication'" :payload="payload" :date-format="dateFormat" />
        <FinancialsTab
          v-else-if="tab === 'financials'"
          :payload="payload"
          :alert-details="alertDetails"
          :date-format="dateFormat"
        >
          <template v-if="$scopedSlots['financials-extra']" #extra><slot name="financials-extra"></slot></template>
        </FinancialsTab>
        <FormsTab v-else-if="tab === 'forms'" :payload="payload" :alert-details="alertDetails" :date-format="dateFormat" />
        <TasksTab
          v-else-if="tab === 'tasks'"
          :tasks="tasks"
          :employees="employees"
          :today="today || ''"
          :timezone="timezone"
          :list-state="tasksState"
        />
      </template>

      <!--
        Persistent tabs: the host's slot stays MOUNTED for the view's whole life and is only hidden —
        for a host tab that owns something that must outlive a tab switch (a listener on a shared store
        slot, say). No host uses it today: the Tasks tab's Firestore listener moved up to the page
        (PatientTasksListener.vue).
      -->
      <div
        v-for="id in persistentTabs"
        v-show="tab === id && payload && patient"
        :key="`persist-${id}`"
        :data-test="`pp-persist-${id}`"
      >
        <slot v-if="payload && patient" :name="`tab-${id}`"></slot>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * One patient — header, action bar, Needs attention, tabs — drawn the same way in two places
 * (Patient Profile overhaul, design 1a, 2026-10-08):
 *
 *   mode 'panel'  inside PatientPanel.vue, the Schedule View slide-over. Data from the patientPanel
 *                 store (/schedule-view/patient); "This visit" and "This appointment" when opened
 *                 from an appointment.
 *   mode 'page'   inside PatientProfilePage.vue, the new Patient Profile route. Data from the page's
 *                 own PATIENT_PROFILE query, shaped by src/utils/patientPanel/profileAdapter.js.
 *
 * Plan: docs/plans/2026-10-08-patient-profile-overhaul-plan.md
 *
 * It fetches nothing and owns no state but what it is given. The host decides what is loaded, what
 * "Needs attention" says (`tiles`) and which tab is open (`tab`, `@tab`). One exception: the Tasks tab
 * draws the Tasks page's own rows (TaskRow) and opens its TaskDrawer, so it dispatches those rows'
 * store actions and the drawer's — the host still owns the Firestore listener behind `tasks`.
 *
 * Host overrides, as slots:
 *   #actions           the action bar (the panel keeps <PatientPanelActions> inside its <aside>)
 *   #avatar            replaces the initials circle (the page's PatientPhotoAvatar)
 *   #header-extra      under the name row (the page's insurance chip, Classic/New link)
 *   #verified-action   inside the header's "Eligibility last verified" chip (the page's Verify /
 *                      Re-check button); unfilled, the chip is exactly as before
 *   #tab-<id>          replaces a tab's body (the page's editable Follow-ups, Family);
 *                      with `persistent-tabs` the slot stays mounted and is only hidden
 *   #financials-extra  appended to the Financials tab (the page's InsurancePanel)
 *   #treatment-side    the Treatment tab's right-hand card when Tx Presenter is off (the page's
 *                      upgrade preview); unfilled, the tab keeps one full-width column
 *   #overview-followups="{ limit }"
 *                      the rows of the Overview tab's Follow-ups card (the page's editable queue,
 *                      first `limit` rows); unfilled, the card keeps the read-only list
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
import PatientPanelHeader from './PatientPanelHeader.vue';
import OverviewTab from './tabs/OverviewTab.vue';
import FollowupsTab from './tabs/FollowupsTab.vue';
import TreatmentTab from './tabs/TreatmentTab.vue';
import AppointmentsTab from './tabs/AppointmentsTab.vue';
import CommunicationTab from './tabs/CommunicationTab.vue';
import FinancialsTab from './tabs/FinancialsTab.vue';
import FormsTab from './tabs/FormsTab.vue';
import TasksTab from './tabs/TasksTab.vue';

/**
 * The design's tabs, in its order. Follow-ups is drawn DISABLED until its rows arrive (`followups`
 * is an array) or the host supplies its own body — the panel's /schedule-view/patient does not
 * carry follow-ups yet, the page's PATIENT_PROFILE query does.
 */
export const VIEW_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'followups', label: 'Follow-ups' },
  { id: 'treatment', label: 'Treatment plan' },
  { id: 'appointments', label: 'Appointments' },
  { id: 'communication', label: 'Communication' },
  { id: 'financials', label: 'Financials' },
  { id: 'forms', label: 'Forms' },
  { id: 'tasks', label: 'Tasks' },
];

export default {
  name: 'PatientProfileView',

  components: {
    PatientPanelHeader, OverviewTab, FollowupsTab, TreatmentTab, AppointmentsTab, CommunicationTab,
    FinancialsTab, FormsTab, TasksTab,
  },

  props: {
    mode: { type: String, default: 'panel', validator: v => v === 'panel' || v === 'page' },

    /** The panel-shaped data ({ patient, scheduled, unscheduledTx, … }), or null while it loads. */
    payload: { type: Object, default: null },
    dataLoading: { type: Boolean, default: false },
    dataError: { type: String, default: '' },
    unavailable: { type: [Object, String, Boolean], default: null },

    /** The live appointment (panel only), or null. */
    appointment: { type: Object, default: null },
    appointmentMeta: { type: Object, default: null },

    insurance: { type: Object, default: null },
    insuranceLoading: { type: Boolean, default: false },
    signedPlans: { type: Array, default: () => [] },
    signedPlansLoading: { type: Boolean, default: false },
    tasks: { type: Array, default: () => [] },
    /** 'ready' | 'loading' | 'failed' — the Tasks tab's list state (the page's listener reports it; the panel's does not). */
    tasksState: { type: String, default: 'ready' },
    employees: { type: Array, default: () => [] },
    /** upcomingFollowups rows, or null when this source does not load them (the panel today). */
    followups: { type: Array, default: null },

    /** [{ key, icon, label, sentence }] — built by the host. */
    tiles: { type: Array, default: () => [] },
    /** The visible alerts' detail objects, by key. */
    alertDetails: { type: Object, default: () => ({}) },
    /** A line under Needs attention (the page says which checks only Schedule View runs). */
    attentionNote: { type: String, default: '' },

    today: { type: String, default: '' },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    timezone: { type: String, default: '' },
    capabilities: { type: Object, default: () => ({}) },

    tab: { type: String, default: 'overview' },
    /** Tabs the host adds after the design's (the page's Family). [{ id, label }] */
    extraTabs: { type: Array, default: () => [] },
    /** Tab ids whose host slot stays mounted (shown/hidden, never destroyed). */
    persistentTabs: { type: Array, default: () => [] },
    profileLink: { type: String, default: '' },
  },

  computed: {
    patient() {
      return (this.payload && this.payload.patient) || null;
    },

    tabList() {
      const hasFollowups = Array.isArray(this.followups) || !!this.$scopedSlots['tab-followups'];
      return VIEW_TABS
        .map(t => (t.id === 'followups' ? Object.assign({}, t, { disabled: !hasFollowups }) : t))
        .concat(this.extraTabs);
    },

    counts() {
      const open = this.tasks.filter(t => t.status !== 'COMPLETED').length;
      const comms = ((this.payload && this.payload.commlog) || []).length;
      const fus = Array.isArray(this.followups) ? this.followups.filter(f => !(Number(f.FollowupStopped) === 1)).length : 0;
      return { tasks: open || '', communication: comms || '', followups: fus || '' };
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.ppv {
  font-family: $pp-font;
  color: $pp-ink;

  // The global `a, .btn, .dropdown-item` rules force Montserrat; the design face wins here.
  ::v-deep a,
  ::v-deep .btn,
  ::v-deep .dropdown-item,
  ::v-deep button {
    font-family: inherit;
  }
}

.pp-tabs {
  display: flex;
  gap: 26px;
  padding: 0 26px;
  background: $pp-surface;
  border-bottom: 1px solid $pp-line;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }
}

.pp-tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0 13px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  font-size: 14px;
  font-weight: 600;
  color: $pp-ink-3;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) { color: $pp-ink; }
  &:disabled { opacity: 0.45; cursor: default; }
}

.pp-tab--on {
  color: $pp-ink;
  font-weight: 700;
  border-bottom-color: $pp-brand-deep;

  .pp-tab__n { background: $pp-brand-tint; color: $pp-brand-deep; }
}

.pp-tab__n {
  padding: 1px 7px;
  border-radius: 99px;
  background: $pp-bg-3;
  color: $pp-ink-2;
  font-size: 11px;
  font-weight: 700;
}

.pp-pad {
  padding: 18px 26px 40px;
}

.pp-state {
  padding: 28px 20px;
  border: 1px dashed $pp-line;
  border-radius: $pp-radius-lg;
  background: $pp-surface;
  text-align: center;
  font-size: 14px;
  color: $pp-ink-3;

  p { margin: 0 0 10px; }
}

.pp-state--error {
  border-style: solid;
  border-color: $pp-red;
  color: $pp-rose-ink;
}

.pp-retry {
  height: 34px;
  padding: 0 14px;
  border: 1px solid currentColor;
  border-radius: $pp-radius;
  background: transparent;
  color: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

// ── Shared by every tab ─────────────────────────────────────────────────────────────────────
// Scoped here and reached through ::v-deep, so the tabs stay free of repeated rules and nothing
// leaks outside the view.

.ppv ::v-deep {
  .pp-cols {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 16px;
    align-items: start;
  }

  // Design 1a's Overview: two equal columns of priority cards.
  .pp-cols--even { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }

  // Treatment plan: Unscheduled (wider, six columns) beside the signed plans.
  .pp-cols--tx { grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); }

  .pp-main,
  .pp-side,
  .pp-stack {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
  }

  .pp-side { gap: 14px; }

  .pp-kv {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 7px 16px;
    margin: 0;
    font-size: 13px;
    align-items: baseline;

    dt { font-weight: 400; color: $pp-ink-3; }
    dd { margin: 0; font-weight: 600; text-align: right; color: $pp-ink; min-width: 0; }
  }

  .pp-kv--spaced { margin-top: 12px; }

  // In a wide card, a right-aligned value sits a screen-width away from its label.
  .pp-kv--left {
    grid-template-columns: 110px 1fr;

    dd { text-align: left; }
  }
  .pp-kv__bad { color: $pp-rose-ink !important; }

  .pp-tbl {
    width: 100%;
    min-width: 520px;
    border-collapse: collapse;
    font-size: 12.5px;

    th {
      padding: 8px 10px;
      border-bottom: 1px solid $pp-line;
      background: $pp-bg-2;
      text-align: left;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.06em;
      // !important only because a global rule has one: `table > thead > tr > th { text-transform:
      // capitalize !important }` would otherwise turn the design's small caps into "Code".
      text-transform: uppercase !important;
      color: $pp-ink-3;
      white-space: nowrap;
    }

    td {
      padding: 9px 10px;
      border-bottom: 1px solid $pp-line-2;
      color: $pp-ink;
      vertical-align: top;
    }

    tbody tr:hover td { background: $pp-bg-2; }
    tr:last-child td { border-bottom: 0; }
  }

  .pp-tbl__num { white-space: nowrap; font-variant-numeric: tabular-nums; }
  // Design 1a: procedure codes in JetBrains Mono.
  .pp-tbl__code { white-space: nowrap; font-family: $pp-mono; font-size: 12px; font-weight: 600; color: $pp-brand; }
  .pp-tbl__note { color: $pp-ink-2; max-width: 280px; }
  .pp-tbl__pri { font-weight: 800; color: $pp-red; }
  .pp-tbl__pri--none { font-weight: 600; color: $pp-ink-4; }

  .pp-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 0 4px 3px 0;
    padding: 3px 9px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }

  .pp-pill--green { background: $pp-green-bg; color: $pp-green-ink; }
  .pp-pill--amber { background: $pp-amber-bg; color: $pp-amber-ink; }
  .pp-pill--rose { background: $pp-rose-bg; color: $pp-rose-ink; }
  .pp-pill--blue { background: $pp-blue-bg; color: $pp-blue-ink; }
  .pp-pill--slate { background: $pp-slate-bg; color: $pp-slate-ink; }

  // Design 1a due pills (follow-ups) and dot pills (broken appointments).
  .pp-pill--due { background: $pp-red-bg; color: $pp-red; }
  .pp-pill--stop { background: $pp-red; color: #ffffff; text-transform: uppercase; letter-spacing: 0.04em; }
  .pp-pill--done { background: $pp-amber-bg-2; color: $pp-amber-deep; }
  .pp-pill--dot::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .pp-pill--missed { background: $pp-red-bg; color: $pp-red; }
  .pp-pill--cancelled { background: $pp-amber-bg-2; color: $pp-amber; }

  .pp-empty {
    margin: 0;
    padding: 18px 12px;
    text-align: center;
    font-size: 13px;
    color: $pp-ink-3;
  }

  .pp-empty--left { padding: 0; text-align: left; }
  .pp-muted { margin: 4px 0 0; font-size: 12px; color: $pp-ink-3; }
  .pp-mono { font-variant-numeric: tabular-nums; letter-spacing: 0.02em; }
  .pp-wrap { overflow-wrap: anywhere; }

  .pp-footnote {
    margin: 0;
    font-size: 12px;
    color: $pp-ink-3;
  }

  // "Showing 1–5 of 12 · View all" under a priority card's table.
  .pp-more {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 2px 0;
    font-size: 12px;
    color: $pp-ink-3;
  }

  .pp-note {
    margin: 12px 0 0;
    padding: 10px 12px;
    border-radius: $pp-radius;
    background: $pp-bg-2;
    border: 1px solid $pp-line-2;
    font-size: 13px;
    color: $pp-ink-2;
    white-space: pre-wrap;
  }

  .pp-note__lbl {
    display: block;
    margin-bottom: 3px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $pp-ink-3;
  }

  .pp-procs {
    margin: 12px 0 0;
    padding: 0;
    list-style: none;

    li {
      padding: 5px 0;
      border-top: 1px solid $pp-line-2;
      font-size: 13px;
      color: $pp-ink;
    }
  }

  .pp-plan + .pp-plan { margin-top: 10px; }

  .pp-plan__title {
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 700;
    color: $pp-ink;
  }

  .pp-plan ~ .pp-kv,
  .pp-empty--left + .pp-kv { margin-top: 10px; }

  .pp-callout {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    padding: 12px 14px;
    border: 1px solid $pp-attn-line;
    border-radius: 13px;
    background: $pp-attn-bg;
  }

  .pp-callout__ic { margin-top: 2px; color: $pp-attn-ink; }
  .pp-callout__t { display: block; font-size: 13.5px; font-weight: 700; color: $pp-ink; }
  .pp-callout__s { display: block; margin-top: 2px; font-size: 12.5px; color: $pp-ink-2; }

  .pp-big {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: $pp-ink;
  }

  .pp-big--bad { color: $pp-rose-ink; }

  .pp-link {
    font-weight: 600;
    color: $pp-brand;

    &:hover { color: $pp-brand-deep; text-decoration: underline; }
  }

  .pp-textbtn {
    margin-top: 10px;
    padding: 0;
    border: 0;
    background: none;
    font-size: 12.5px;
    font-weight: 600;
    color: $pp-brand;
    cursor: pointer;

    &:hover { color: $pp-brand-deep; text-decoration: underline; }
  }

  .pp-more .pp-textbtn { margin-top: 0; font-size: 12px; }
  // Under a table card (no body padding).
  .pp-more--pad { padding: 10px 16px 12px; border-top: 1px solid $pp-line-2; }
  // A two-column list inside a narrow priority card: no sideways scroll.
  .pp-tbl--narrow { min-width: 0; }

  .pp-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }

  // Design 1a filter pills: an active pill is teal-tinted with a teal border.
  .pp-chip {
    height: 32px;
    padding: 0 13px;
    border: 1px solid $pp-line;
    border-radius: 99px;
    background: $pp-surface;
    font-size: 12px;
    font-weight: 600;
    color: $pp-ink-2;
    cursor: pointer;

    &:hover { background: $pp-bg-2; }
  }

  .pp-chip--on {
    background: $pp-brand-soft;
    border-color: $pp-brand-deep;
    color: $pp-brand-deep;

    &:hover { background: $pp-brand-soft; }
  }

  .pp-msgs {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .pp-msg {
    display: flex;
    gap: 11px;
    padding: 12px 14px;
    border-bottom: 1px solid $pp-line-2;

    &:last-child { border-bottom: 0; }
  }

  .pp-msg__ch {
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 13px;
    background: $pp-ink-3;
  }

  .pp-msg__ch--phone { background: #2f6fb3; }
  .pp-msg__ch--text { background: #1b8a5a; }
  .pp-msg__ch--email { background: #6a4fbf; }

  .pp-msg__main { flex: 1 1 auto; min-width: 0; }

  .pp-msg__top {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;

    .pp-pill { margin: 0; }
  }

  .pp-msg__type { font-size: 12.5px; font-weight: 700; color: $pp-ink; }
  .pp-msg__who { font-size: 12px; color: $pp-ink-3; }
  .pp-msg__dir { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: $pp-brand; }

  .pp-msg__when {
    margin-left: auto;
    font-size: 11px;
    color: $pp-ink-3;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .pp-msg__body {
    margin: 4px 0 0;
    font-size: 13px;
    color: $pp-ink-2;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  // Design 1a follow-up rows (Overview + the Follow-ups tab).
  .pp-fu {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid $pp-line-2;

    &:last-child { border-bottom: 0; }
  }

  .pp-fu__main { flex: 1 1 auto; min-width: 0; }
  .pp-fu__type { margin: 0; font-size: 13.5px; font-weight: 700; color: $pp-ink; }
  .pp-fu__note { margin: 2px 0 0; font-size: 12.5px; color: $pp-ink-2; overflow-wrap: anywhere; }
  .pp-fu__meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 8px; margin-top: 6px; font-size: 12px; color: $pp-ink-3; }
  .pp-fu__meta .pp-pill { margin: 0; }
}

// The two-column Overview/Financials stacks below the design's own 1100px line.
@media (max-width: $pp-stack-max) {
  .pp-tabs { gap: 20px; padding: 0 20px; }
  .pp-pad { padding: 16px 20px 36px; }

  .ppv ::v-deep {
    .pp-cols,
    .pp-cols--even,
    .pp-cols--tx { grid-template-columns: minmax(0, 1fr); }

    .pp-side {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    }
  }
}

// Phone: tabs pinned at the top of the scroll (under the panel's back bar: the shell sets
// --ppv-tabs-top), the design's phone artboard.
@media (max-width: $pp-phone-max) {
  .pp-tabs {
    position: sticky;
    top: var(--ppv-tabs-top, 0);
    z-index: 4;
    gap: 0;
    padding: 0 8px;
  }

  .pp-tab { padding: 13px 12px; font-size: 13.5px; min-height: 46px; }
  // The extra bottom room lets the last card scroll clear of the AI agent's floating button,
  // which sits over the bottom-right of every phone screen.
  .pp-pad { padding: 14px 16px 150px; }

  .ppv ::v-deep {
    .pp-side { display: flex; }
    .pp-chip { height: 38px; font-size: 13px; }
    .pp-kv { font-size: 12.5px; }
  }
}
</style>
